import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Input, Empty, Table, Td } from "../../components/UI";
import { FLabel } from "../engagement/FormControls";
import { CocoaSelect } from "../../components/CocoaSelect";
import { Plus, Edit3, Trash2, ShieldCheck, AlertTriangle, Calendar } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

// Business Associate Agreement tracker (audit item 84). Every vendor with
// PHI access must have a BAA on file. This dashboard shows active BAAs,
// contracts expiring in the next 60 days, and allows admin CRUD.

const STATUS_OPTIONS = [["active", "Active"], ["expired", "Expired"], ["pending", "Pending"], ["terminated", "Terminated"]];

const EMPTY = { vendor_name: "", vendor_contact: "", vendor_email: "",
  contract_start: "", contract_end: "", phi_scope: "", status: "active", notes: "" };

export default function BaaTrackerSection() {
  const [data, setData] = useState({ items: [], active: 0, expiring_in_60_days: 0 });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/baa-records").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (row) => { setForm({ ...EMPTY, ...row }); setEditing(row.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.vendor_name.trim()) return toast.error("Vendor name is required");
    setBusy(true);
    try {
      if (editing === "new") await api.post("/baa-records", form);
      else await api.put(`/baa-records/${editing}`, form);
      toast.success("BAA record saved");
      setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete BAA for ${row.vendor_name}?`)) return;
    try { await api.delete(`/baa-records/${row.id}`); toast.success("BAA deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="settings-baa-tracker">
      <div className="grid gap-3 md:grid-cols-3">
        <Card className="p-4"><div className="flex items-center gap-2 text-[#7B6157]"><ShieldCheck size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Active BAAs</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#2E7D32]" data-testid="baa-stat-active">{data.active}</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-[#7B6157]"><AlertTriangle size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Expiring soon</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#8D5A00]" data-testid="baa-stat-expiring">{data.expiring_in_60_days}</div>
          <div className="text-xs text-[#7B6157]">within 60 days</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-[#7B6157]"><Calendar size={13} /><span className="text-[10px] font-bold uppercase tracking-wide">Total records</span></div>
          <div className="mt-2 text-2xl font-heading font-bold text-[#3E2723]" data-testid="baa-stat-total">{data.total}</div></Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between border-b border-[#F1EEE7] px-5 py-3">
          <div>
            <h3 className="font-heading text-base font-bold text-[#3E2723]">Business Associate Agreements</h3>
            <p className="mt-0.5 text-[11px] text-[#7B6157]">Track every vendor that touches PHI (analytics, telehealth, e-fax, cloud, and more).</p>
          </div>
          <Btn data-testid="baa-new-btn" onClick={openNew} className="px-3 py-1.5 text-xs"><Plus size={13} /> New BAA</Btn>
        </div>
        {data.items.length === 0 ? <Empty text="No BAA records yet. Click New BAA to add your first vendor agreement." /> : (
          <Table headers={["Vendor", "PHI scope", "Contract term", "Status", "Actions"]}>
            {data.items.map((r) => (
              <tr key={r.id} className="border-b border-[#EFEBE0] last:border-0" data-testid={`baa-row-${r.id}`}>
                <Td>
                  <div className="text-sm font-semibold text-[#3E2723]">{r.vendor_name}</div>
                  <div className="text-[11px] text-[#7B6157]">{r.vendor_contact}{r.vendor_email ? " · " + r.vendor_email : ""}</div>
                </Td>
                <Td className="text-xs text-[#5D4037] max-w-xs">{r.phi_scope || "-"}</Td>
                <Td className="text-xs tabular-nums">{r.contract_start || "-"}{r.contract_end ? " → " + r.contract_end : ""}</Td>
                <Td>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    r.status === "active" ? "bg-[#E8F5E9] text-[#2E7D32]"
                    : r.status === "expired" ? "bg-[#FFEBEE] text-[#C62828]"
                    : r.status === "pending" ? "bg-[#FFF3E0] text-[#8D5A00]"
                    : "bg-[#F5F2EB] text-[#5D4037]"}`}>{r.status}</span>
                </Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <button data-testid={`baa-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 rounded-lg text-[#5D4037] hover:bg-[#F5F2EB]"><Edit3 size={13} /></button>
                    <button data-testid={`baa-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-lg text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {editing && (
        <div className="fixed inset-0 z-[80] bg-[#3E2723]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={cancel}>
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="baa-editor">
            <h3 className="mb-4 font-heading text-lg font-bold text-[#3E2723]">{editing === "new" ? "New BAA" : "Edit BAA"}</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div><FLabel>Vendor name *</FLabel>
                <Input data-testid="baa-vendor-name" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} /></div>
              <div><FLabel>Vendor contact</FLabel>
                <Input data-testid="baa-vendor-contact" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} /></div>
              <div><FLabel>Vendor email</FLabel>
                <Input data-testid="baa-vendor-email" value={form.vendor_email} onChange={(e) => setForm({ ...form, vendor_email: e.target.value })} /></div>
              <div><FLabel>Status</FLabel>
                <CocoaSelect testId="baa-status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUS_OPTIONS} /></div>
              <div><FLabel>Contract start</FLabel>
                <Input type="date" data-testid="baa-contract-start" value={form.contract_start} onChange={(e) => setForm({ ...form, contract_start: e.target.value })} /></div>
              <div><FLabel>Contract end</FLabel>
                <Input type="date" data-testid="baa-contract-end" value={form.contract_end} onChange={(e) => setForm({ ...form, contract_end: e.target.value })} /></div>
              <div className="md:col-span-2"><FLabel>PHI scope</FLabel>
                <Input data-testid="baa-phi-scope" placeholder="What PHI does this vendor process?" value={form.phi_scope} onChange={(e) => setForm({ ...form, phi_scope: e.target.value })} /></div>
              <div className="md:col-span-2"><FLabel>Notes</FLabel>
                <textarea data-testid="baa-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] focus:ring-2 focus:ring-[#8D6E63]/40" /></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Btn variant="outline" data-testid="baa-cancel" onClick={cancel}>Cancel</Btn>
              <Btn data-testid="baa-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save BAA"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Session-timeout config sub-section (audit item 82)
export function SessionTimeoutSection() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings/session-timeout").then((r) => setCfg(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!cfg) return <div className="py-8 text-center text-sm text-[#84675B]">Loading…</div>;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/session-timeout", cfg);
      setCfg(r.data);
      toast.success("Session timeout saved. Applies on next login.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-6" data-testid="settings-session-timeout">
      <h3 className="mb-1 font-heading text-base font-bold text-[#3E2723]">Session timeout</h3>
      <p className="mb-4 text-[11px] text-[#7B6157]">Applies to every staff session on this tenant. HIPAA best practice: 15 minutes idle, 8 hours absolute.</p>
      <div className="grid gap-4 md:grid-cols-2 max-w-lg">
        <div><FLabel>Idle timeout (minutes)</FLabel>
          <Input type="number" min={1} max={240} data-testid="st-idle-input" value={cfg.idle_minutes || 15}
            onChange={(e) => setCfg({ ...cfg, idle_minutes: Number(e.target.value) })} /></div>
        <div><FLabel>Absolute timeout (hours)</FLabel>
          <Input type="number" min={1} max={24} data-testid="st-absolute-input" value={cfg.absolute_hours || 8}
            onChange={(e) => setCfg({ ...cfg, absolute_hours: Number(e.target.value) })} /></div>
      </div>
      <div className="mt-5"><Btn data-testid="st-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Timeout"}</Btn></div>
    </Card>
  );
}
